import React from "react";
import { AppIcon, CheckIcon, MicIcon, StopIcon } from "./icons";

/**
 * HomepageSections.js
 *
 * Marketing/recruitment content that now wraps the real sign-in/sign-up
 * form in OnboardingScreen.js, turning the logged-out landing experience
 * into an actual homepage instead of a bare auth form. Copy source:
 * `.claude/agent-memory/ethan-copywriter/homepage_copy_final_2026_09_27.md`
 * (Bernardo-approved, 2026-09-27 corrections: 50+/any level/no location
 * restriction/4 weeks/email-only contact).
 *
 * Every section here is presentational only — no Supabase calls, no auth
 * state. All auth logic stays in OnboardingScreen.js untouched. Design
 * tokens are the "Wine & Stone" system from tailwind.config.js: indigo is
 * the primary brand accent; rose is used sparingly for the one place it's
 * established elsewhere in the app — the Listening/"You" moments — per
 * jonny-web-designer's memory, not for decorative section rhythm.
 *
 * 2026-09-27 hero fix: the hero previously shipped as a rounded chat-bubble
 * UI for the Tutor/You exchange. That was never the approved design — Jonny
 * confirmed against the real Figma file (z9nQiAm48ZaaNu1tAU4kSr) that the
 * approved hero is the real-app-screen Before/After (page "07 Hero - Real UI
 * motion concept", node 45:3: two device frames, "Before — Ready to talk"
 * and "After — Listening"), and that if the Tutor/You dialogue is kept at
 * all, it must use a restrained transcript style (colored left accent-bar +
 * speaker label, no bubbles/tails/avatars — page "06 Hero Sample
 * Conversation Card"), specifically to avoid reading as a generic
 * AI-chat-bubble product. HeroDeviceDemo below replaces the old bubble hero;
 * the dialogue itself now lives lower on the page as HomepageSampleTranscript
 * in the correct transcript style.
 */

// --- Header --------------------------------------------------------------
export function HomepageHeader({ t, language, setLanguage, onLogInClick }) {
  return (
    <header className="sticky top-0 z-10 border-b border-ink-200 bg-paper-50/80 backdrop-blur">
      <div className="mx-auto max-w-5xl px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AppIcon />
          <span className="font-bold text-lg md:text-xl">{t('app.title')}</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            className="px-3 py-2 rounded-control border border-ink-200 bg-paper-100 text-sm font-semibold hover:bg-paper-100/70 transition"
            onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
            aria-label="Change language"
          >
            {language === 'es' ? '🇬🇧 English' : '🇪🇸 Español'}
          </button>
          <button
            onClick={onLogInClick}
            className="text-label-button font-semibold text-indigo-700 underline hover:text-indigo-900 whitespace-nowrap"
          >
            {t('homepage.header.logIn')}
          </button>
        </div>
      </div>
    </header>
  );
}

// --- Hero ------------------------------------------------------------------
// 2026-09-27 round 4: bg-paper-50 (near-white) swapped for bg-indigo-100 (a
// soft full-bleed tint) per jonny-web-designer's structural fix, mocked
// directly in Figma z9nQiAm48ZaaNu1tAU4kSr, page "08 Landing Page (EN)",
// frame 53:3. Same restrained palette, no new hue — just gives the hero real
// background weight instead of reading as near-white.
export function HomepageHero({ t, onRequestInvitation }) {
  return (
    <section className="bg-indigo-100 px-4 py-16 md:py-24">
      <div className="mx-auto max-w-5xl grid md:grid-cols-2 gap-12 items-center">
        <div className="text-center md:text-left">
          <h1 className="text-display-h1 md:text-4xl font-extrabold text-ink-900 mb-5">
            {t('homepage.hero.headline')}
          </h1>
          <p className="text-body-lg text-ink-600 mb-6">
            {t('homepage.hero.subheading')}
          </p>
          <button
            onClick={onRequestInvitation}
            className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white text-label-button font-semibold rounded-control shadow-lg transition"
          >
            {t('auth.signUp')}
          </button>
        </div>

        {/* Real-app Before/After device demo — the approved hero visual
            (Figma page "07 Hero - Real UI motion concept", node 45:3), not
            an invented chat UI. Content inside each device frame is pulled
            from TalkView.js/ListeningView.js's actual current copy, colors
            and layout (see HeroDeviceDemo below) so it never drifts from
            what's really shipped. */}
        <HeroDeviceDemo t={t} />
      </div>
    </section>
  );
}

/**
 * HeroDeviceDemo — the approved hero visual replacing the old chat-bubble
 * card. Two small device frames built from the real TalkView.js "Ready to
 * talk?" screen and the real ListeningView.js "Listening" screen (same
 * copy via t(), same indigo-600 mic / rose-700 stop-button colors, same
 * TIER_LIMITS.free minutes) — matches Figma z9nQiAm48ZaaNu1tAU4kSr, page
 * "07 Hero - Real UI motion concept", node 45:3, confirmed by screenshot
 * before building this.
 *
 * Motion: both frames always exist in the DOM as a static side-by-side
 * Before/After pair — that is the entire experience for anyone with
 * `prefers-reduced-motion: reduce` (WCAG 2.3.3; this audience may include
 * vestibular sensitivity, so there is no "reduced but still moving"
 * version, only on/off, matching the rest of this app's motion policy in
 * index.css). Only for visitors with no motion preference set does the
 * CSS in index.css (`.hero-demo` rules) collapse the two frames into one
 * auto-looping animated demo — the same 200ms opacity+scale ease-out and
 * slow pulse-breathe timing as the real app's own Talk→Listening
 * transition (`.animate-state-in` / `.animate-listening-pulse`, reused
 * as-is, not re-implemented), never a bounce/spring. The loop runs a
 * handful of cycles and then rests on the Ready state, rather than
 * looping forever, per WCAG 2.2.2 (auto-updating content should not run
 * indefinitely with no way to stop it) — see index.css for the exact
 * iteration count.
 *
 * The "Before —" / "After —" `.hero-demo-label` captions only render in
 * the static side-by-side (reduced-motion) layout, where they sit next to
 * each other with no overlap. In the animated layout the two panels are
 * absolutely stacked at the exact same position, and only the *panel*
 * (device card) fades — a bare text label has no opaque background to
 * hide the other label behind, so if both labels stayed rendered there
 * they'd be two overlapping, illegible strings of text during the
 * cross-fade (this shipped once — see index.css for the fix: `.hero-demo-
 * label { display: none }` under the no-preference media query). The
 * animated version relies on the in-card headings ("Ready to Practice?" /
 * "Listening...", already real app copy) to convey state instead.
 */
function HeroDeviceDemo({ t }) {
  return (
    <div className="hero-demo" aria-hidden="true">
      <div className="hero-demo-panel hero-demo-ready">
        <p className="hero-demo-label text-caption font-semibold text-ink-600 mb-2 text-center">
          {t('homepage.heroDemo.beforeLabel')}
        </p>
        <div className="hero-demo-device rounded-3xl border-[6px] border-ink-900 bg-paper-50 shadow-xl p-2">
          <div className="rounded-2xl overflow-hidden flex flex-col h-[380px]">
            {/* No tier/usage-limit banner here, deliberately - matches the
                Listening panel's fix below. Any "X of Y minutes" or "Free
                tier" badge contradicts this page's own "Free for the Whole
                Study" message a few sections down. Real logged-in users
                still see the real banner in the actual TalkView.js/
                ListeningView.js - this is the marketing mockup only. */}
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <h3 className="text-body-lg font-bold text-ink-900 mb-1">{t('talk.readyTitle')}</h3>
              <p className="text-caption text-ink-600 mb-4">{t('talk.readySubtitle')}</p>
              <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <MicIcon size={32} className="text-white" />
              </div>
            </div>
            <div className="border-t border-ink-200 px-4 py-3">
              <p className="text-caption font-semibold text-ink-900 mb-2">{t('talk.todaysConversation')}</p>
              <div className="rounded-card bg-paper-100 border border-ink-200 px-3 py-2">
                <p className="text-caption text-ink-900 leading-snug">{t('talk.defaultGreeting')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-demo-arrow" aria-hidden="true">
        <span className="text-indigo-700 text-2xl leading-none">→</span>
        <p className="text-caption text-ink-600 mt-1 max-w-[120px]">{t('homepage.heroDemo.caption')}</p>
      </div>

      <div className="hero-demo-panel hero-demo-listening">
        <p className="hero-demo-label text-caption font-semibold text-ink-600 mb-2 text-center">
          {t('homepage.heroDemo.afterLabel')}
        </p>
        <div className="hero-demo-device rounded-3xl border-[6px] border-ink-900 bg-paper-50 shadow-xl p-2">
          {/* No usage-limit banner here on purpose. This is a marketing
              mockup, not a live logged-in session — study participants don't
              have TIER_LIMITS' usage caps, so showing a "2 minutes
              remaining" countdown (as the real ListeningView.js does for an
              actual free-tier user) would directly contradict this page's
              own "Free for the Whole Study" message a few sections down.
              Fixed 2026-09-27 per jonny-web-designer's review; ListeningView.js
              itself is untouched and still shows the real countdown for real
              users on a real tier. */}
          <div className="rounded-2xl overflow-hidden flex flex-col h-[380px]">
            <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
              <h3 className="text-body-lg font-bold text-ink-900 mb-1">{t('talk.listening')}</h3>
              <p className="text-caption text-ink-600 mb-4">{t('talk.aiResponse')}</p>
              <div className="relative flex items-center justify-center">
                <span aria-hidden="true" className="absolute w-24 h-24 rounded-full bg-rose-700/40 animate-listening-pulse" />
                <div className="relative w-20 h-20 bg-rose-700 rounded-full flex items-center justify-center shadow-lg">
                  <StopIcon />
                </div>
              </div>
            </div>
            <div className="hero-demo-transcript-line border-t border-ink-200 px-4 py-3">
              <p className="text-caption font-semibold text-ink-600 mb-1">{t('talk.liveTranscript')}</p>
              <p className="text-caption text-ink-900 leading-snug">{t('homepage.sample.line2')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Sample transcript (real Tutor/You exchange, restrained transcript
// style — NOT chat bubbles) ----------------------------------------------
// Per Jonny's approved Figma spec (page "06 Hero Sample Conversation
// Card"): a colored left accent-bar + a small speaker label above the
// line + the line itself, stacked vertically. No bubble shapes, no bubble
// tails, no avatars — deliberately, to avoid reading as a generic
// AI-chat-bubble product (Bernardo had already rejected other options for
// looking too "AI-made").
//
// 2026-09-27 round 4: NOT rendered on the homepage anymore. The real-UI
// HeroDeviceDemo hero (above) now shows its own live-transcript line using
// the same demo exchange (homepage.sample.line2), so rendering this section
// too meant showing the identical Tutor/You exchange twice on one page —
// flagged by jonny-web-designer as redundant/thin rather than rich. Left
// this component and its `homepage.sample.*` locale keys in place
// (unrendered, not deleted) in case the standalone transcript card is wanted
// again later — see OnboardingScreen.js, which no longer imports or calls
// HomepageSampleTranscript.
function TranscriptLine({ speaker, text, isTutor }) {
  return (
    <li className={`pl-4 border-l-4 ${isTutor ? 'border-indigo-700' : 'border-rose-800'}`}>
      <p className={`text-caption font-semibold mb-1 ${isTutor ? 'text-indigo-700' : 'text-rose-800'}`}>{speaker}</p>
      <p className="text-body-base text-ink-900 leading-relaxed">{text}</p>
    </li>
  );
}

export function HomepageSampleTranscript({ t }) {
  return (
    <section className="bg-white px-4 py-16">
      <div className="mx-auto max-w-2xl">
        <div className="bg-paper-50 border border-ink-200 rounded-card shadow-sm p-6 md:p-8 text-left">
          <ul className="space-y-5">
            <TranscriptLine speaker={t('homepage.sample.tutorLabel')} text={t('homepage.sample.line1')} isTutor />
            <TranscriptLine speaker={t('homepage.sample.youLabel')} text={t('homepage.sample.line2')} />
            <TranscriptLine speaker={t('homepage.sample.tutorLabel')} text={t('homepage.sample.line3')} isTutor />
            <TranscriptLine speaker={t('homepage.sample.youLabel')} text={t('homepage.sample.line4')} />
          </ul>
          <p className="mt-6 text-caption text-ink-600 text-center">{t('homepage.sample.caption')}</p>
        </div>
      </div>
    </section>
  );
}

// --- How It Works -----------------------------------------------------------
export function HomepageHowItWorks({ t }) {
  const steps = [
    { title: t('homepage.howItWorks.step1Title'), desc: t('homepage.howItWorks.step1Desc') },
    { title: t('homepage.howItWorks.step2Title'), desc: t('homepage.howItWorks.step2Desc') },
    { title: t('homepage.howItWorks.step3Title'), desc: t('homepage.howItWorks.step3Desc') },
  ];
  return (
    <section className="bg-white px-4 py-16">
      <div className="mx-auto max-w-5xl">
        <h2 className="text-heading-h3 font-bold text-ink-900 text-center mb-10">
          {t('homepage.howItWorks.title')}
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <div key={i} className="text-center">
              <div className="mx-auto w-14 h-14 mb-4 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-xl font-bold">
                {i + 1}
              </div>
              <h3 className="text-body-lg font-semibold text-ink-900 mb-2">{step.title}</h3>
              <p className="text-body-base text-ink-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Why This Works ----------------------------------------------------------
// 2026-09-27 round 4: converted to a full-bleed dark indigo-700 band (was
// bg-paper-50 with paper-100 cards + a small indigo-600 top-rule accent).
// This is the structural fix from jonny-web-designer's review — the page's
// only other full-bleed dark section was the Signup CTA band near the
// bottom, so a visitor scrolled through 4+ near-white sections before
// hitting any real color weight. Mocked directly in Figma
// z9nQiAm48ZaaNu1tAU4kSr, page "08 Landing Page (EN)", frame 53:3: white
// heading/subtitle, white item titles, paper-100 (not pure white) item
// descriptions, and the per-item indigo-600 top-rule/card fill dropped since
// the section color itself now carries the moment.
export function HomepageWhyThisWorks({ t }) {
  const items = [
    { title: t('homepage.whyThisWorks.item1Title'), desc: t('homepage.whyThisWorks.item1Desc') },
    { title: t('homepage.whyThisWorks.item2Title'), desc: t('homepage.whyThisWorks.item2Desc') },
    { title: t('homepage.whyThisWorks.item3Title'), desc: t('homepage.whyThisWorks.item3Desc') },
    { title: t('homepage.whyThisWorks.item4Title'), desc: t('homepage.whyThisWorks.item4Desc') },
  ];
  return (
    <section className="bg-indigo-700 px-4 py-16">
      <div className="mx-auto max-w-5xl">
        <h2 className="text-heading-h3 font-bold text-white text-center mb-4">
          {t('homepage.whyThisWorks.title')}
        </h2>
        <p className="text-body-lg text-white text-center mb-10 max-w-2xl mx-auto">
          {t('homepage.whyThisWorks.subtitle')}
        </p>
        <div className="grid sm:grid-cols-2 gap-6">
          {items.map((item, i) => (
            <div key={i} className="text-left">
              <h3 className="text-body-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-body-base text-paper-100">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- Who Can Take Part -------------------------------------------------------
export function HomepageEligibility({ t }) {
  const checks = [
    t('homepage.eligibility.check1'),
    t('homepage.eligibility.check2'),
    t('homepage.eligibility.check3'),
    t('homepage.eligibility.check4'),
  ];
  return (
    <section className="bg-white px-4 py-16">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-heading-h3 font-bold text-ink-900 text-center mb-10">
          {t('homepage.eligibility.title')}
        </h2>
        <ul className="space-y-4 mb-8">
          {checks.map((check, i) => (
            <li key={i} className="flex items-start gap-3 text-left">
              <CheckIcon />
              <span className="text-body-lg text-ink-900">{check}</span>
            </li>
          ))}
        </ul>
        <div className="bg-indigo-100 rounded-card p-6 text-left">
          <p className="text-body-lg font-semibold text-ink-900 mb-1">{t('homepage.eligibility.timeTitle')}</p>
          <p className="text-body-base text-ink-600">{t('homepage.eligibility.timeDesc')}</p>
        </div>
      </div>
    </section>
  );
}

// --- What You Get -------------------------------------------------------------
export function HomepageWhatYouGet({ t }) {
  const items = [
    { title: t('homepage.whatYouGet.item1Title'), body: <p className="text-body-base text-ink-600">{t('homepage.whatYouGet.item1Desc')}</p> },
    { title: t('homepage.whatYouGet.item2Title'), body: <p className="text-body-base text-ink-600">{t('homepage.whatYouGet.item2Desc')}</p> },
    {
      title: t('homepage.whatYouGet.item3Title'),
      body: (
        <p className="text-body-base text-ink-600">
          {t('homepage.whatYouGet.item3DescPrefix')}{' '}
          <a href="mailto:bernardm@ucm.es" className="text-indigo-700 underline hover:text-indigo-900">bernardm@ucm.es</a>{' '}
          {t('homepage.whatYouGet.item3DescSuffix')}
        </p>
      ),
    },
    { title: t('homepage.whatYouGet.item4Title'), body: <p className="text-body-base text-ink-600">{t('homepage.whatYouGet.item4Desc')}</p> },
  ];
  return (
    <section className="bg-paper-50 px-4 py-16">
      <div className="mx-auto max-w-5xl">
        <h2 className="text-heading-h3 font-bold text-ink-900 text-center mb-10">
          {t('homepage.whatYouGet.title')}
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {items.map((item, i) => (
            <div key={i} className="bg-paper-100 rounded-card border-l-4 border-indigo-600 p-6 text-left">
              <h3 className="text-body-lg font-semibold text-ink-900 mb-2">{item.title}</h3>
              {item.body}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --- CTA band (dark, immediately above the real sign-in/sign-up form) -------
export function HomepageCtaBand({ t }) {
  return (
    <section id="signin-intro" className="bg-indigo-700 px-4 py-16 text-center">
      <div className="mx-auto max-w-2xl">
        <h2 className="text-heading-h3 font-bold text-white mb-4">{t('homepage.ctaBand.heading')}</h2>
        <p className="text-body-lg text-indigo-100 mb-4">{t('homepage.ctaBand.supporting')}</p>
        <p className="text-body-base text-indigo-100">
          {t('homepage.ctaBand.directContactPrefix')}{' '}
          <a href="mailto:bernardm@ucm.es" className="text-white underline hover:text-indigo-100">bernardm@ucm.es</a>.
        </p>
      </div>
    </section>
  );
}

// --- Footer -------------------------------------------------------------------
export function HomepageFooter({ t }) {
  return (
    <footer className="bg-ink-900 text-paper-100 px-4 py-10">
      <div className="mx-auto max-w-5xl text-center space-y-2 text-body-base">
        <p>{t('homepage.footer.credit')}</p>
        <p>
          {t('homepage.footer.contactPrefix')}{' '}
          <a href="mailto:bernardm@ucm.es" className="underline hover:text-white">bernardm@ucm.es</a>.
        </p>
        <p>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
            {t('account.privacy.privacyLink')}
          </a>
        </p>
      </div>
    </footer>
  );
}
